import { useAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { brandGroupIdAtom } from '~/atoms'
import {
  BrandGroupSelector,
  type BrandGroupSelectorProps,
} from '~/components/features/BrandGroupSelector'
import { useLogEvent } from '~/hooks/useLogEvent'
import {
  type BrandGroup,
  useSurveyQueryAvailableBrandGroups,
} from '~/hooks/useSurveyQuery'
import { ensureExists } from '~/utils'

export type SharedBrandGroupSelectorProps = {
  'data-testid'?: string
  onChange?: (brandGroup: BrandGroup) => void
}

export const SharedBrandGroupSelector = ({
  'data-testid': dataTestId = 'components-features-shared-brand-group-selector',
  onChange,
}: SharedBrandGroupSelectorProps) => {
  const { t } = useTranslation()
  const brandGroups = useSurveyQueryAvailableBrandGroups()

  const [brandGroupId, setBrandGroupId] = useAtom(brandGroupIdAtom)

  const logEvent = useLogEvent()
  const handleChange = React.useCallback<
    EH<BrandGroupSelectorProps<BrandGroup>, 'onChange'>
  >(
    (brandGroupId) => {
      setBrandGroupId(brandGroupId)

      const brandGroup = ensureExists(
        brandGroups.find(({ id }) => id === brandGroupId),
        t('assertion_failed'),
      )
      onChange?.(brandGroup)

      logEvent('Select brand group', {
        eventProperties: [{ key: 'name', value: brandGroup.name }],
        withSuffix: true,
      })
    },
    [brandGroups, logEvent, onChange, setBrandGroupId, t],
  )

  return (
    <BrandGroupSelector
      brandGroups={brandGroups}
      data-testid={dataTestId}
      value={brandGroupId}
      width={360}
      onChange={handleChange}
    />
  )
}
